import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useStore } from '../../../store/useStore';
import { Promotion, PromotionType, Screen, LinkedProduct } from '../../../shared/types';
import { validateImportedData } from '../services/exportImportService';
import { usePromotionSync } from '../hooks/usePromotionSync';
import { useCategories } from '../hooks/useCategories';
import { usePromotionPagination } from '../hooks/usePromotionPagination';
import { PromotionActions } from '../components/PromotionActions';
import { PromotionFilters } from '../components/PromotionFilters';
import { PromotionList } from '../components/PromotionList';
import { PromotionPagination } from '../components/PromotionPagination';
import { PromotionFormModal } from '../components/PromotionFormModal';
import { ScreenSelectorModal } from '../../screens/components/ScreenSelectorModal';
import { useBannersOtimizado } from '../../banners/hooks/useBanners';
import { bannerService } from '../../banners/services/bannerService';

type SortOption = 'name-asc' | 'name-desc' | 'price-asc' | 'price-desc';

const PromotionsPage = () => {
    const { promotions, screens, addPromotion, updatePromotion, updateScreen } = useStore();
    const { syncWithCheckout, loading: isSyncing } = usePromotionSync();
    const { categorias } = useCategories();
    // Usar hook otimizado - NÃO carregar automaticamente (autoLoad=false)
    // Telas só serão carregadas quando necessário (vincular produtos)
    const { telas: telasOtimizadasHook, loading: loadingTelasHook, carregarTelas } = useBannersOtimizado(false);
    
    // Estado local para telas otimizadas (sem produtos) quando for vincular produtos
    const [telasOtimizadas, setTelasOtimizadas] = useState<any[]>([]);
    const [loadingTelas, setLoadingTelas] = useState(false);
    
    // Carregar telas básicas para o dropdown quando o componente monta
    useEffect(() => {
        const carregarTelasParaDropdown = async () => {
            // Só carregar se ainda não foram carregadas
            if (telasOtimizadas.length === 0 && !loadingTelas) {
                setLoadingTelas(true);
                try {
                    // Usar listarTelasOtimizado() que retorna telas com apenas IDs de produtos (leve)
                    const telas = await bannerService.listarTelasOtimizado();
                    setTelasOtimizadas(telas);
                } catch (err) {
                    console.error('Erro ao carregar telas para dropdown:', err);
                } finally {
                    setLoadingTelas(false);
                }
            }
        };
        
        carregarTelasParaDropdown();
    }, []); // Executar apenas uma vez ao montar

    // Estado local para rastrear produtos vinculados/desvinculados recentemente (sem recarregar da API)
    const [recentlyLinkedProducts, setRecentlyLinkedProducts] = useState<Set<number>>(new Set());
    const [recentlyUnlinkedProducts, setRecentlyUnlinkedProducts] = useState<Set<number>>(new Set());

    // UI State
    const [showForm, setShowForm] = useState(false);
    const [showScreenSelector, setShowScreenSelector] = useState(false);
    const [screenSelectorMode, setScreenSelectorMode] = useState<'link' | 'unlink'>('link');
    const [selectedPromoIds, setSelectedPromoIds] = useState<number[]>([]);
    const [singlePromoId, setSinglePromoId] = useState<number | null>(null);
    const [verificandoVinculacao, setVerificandoVinculacao] = useState(false);

    // Filter State
    const [searchTerm, setSearchTerm] = useState('');
    const [barcode, setBarcode] = useState('');
    const [internalCode, setInternalCode] = useState('');
    const previousSearchTermRef = useRef<string>('');
    const previousBarcodeRef = useRef<string>('');
    const previousInternalCodeRef = useRef<string>('');
    const [selectedCategory, setSelectedCategory] = useState<'all' | number>('all');
    const [screenFilter, setScreenFilter] = useState<'all' | 'linked' | 'unlinked'>('all');
    const [selectedScreenFilter, setSelectedScreenFilter] = useState<number | 'all'>('all');
    const [sortOption, setSortOption] = useState<SortOption>('name-asc');
    const [promotionKindFilter, setPromotionKindFilter] = useState<'all' | 'product' | 'video'>('all');
    const [promotionFilter, setPromotionFilter] = useState<boolean>(false); // Filtro de produtos em promoção
    const [selectedUnitTypes, setSelectedUnitTypes] = useState<string[]>([]); // Filtro de tipos de unidade (array de siglas)

    // Lista completa de produtos da tela selecionada (para modo de filtro por tela)
    const [screenProducts, setScreenProducts] = useState<Promotion[]>([]);
    const [loadingScreenProducts, setLoadingScreenProducts] = useState(false);

    // Estado para tamanho da página
    const [pageSize, setPageSize] = useState<number>(50);

    // Paginação hook - busca produtos e retorna IDs buscados
    // autoSearch=false para não buscar automaticamente quando searchTerm mudar
    const {
        produtosBuscados,
        vinculacoesProdutos, // Mapa de produtoId -> vinculadoATela do JSON
        loading: loadingProdutos,
        currentPage,
        hasNextPage,
        hasPreviousPage,
        goToNextPage,
        goToPreviousPage,
        triggerSearch
    } = usePromotionPagination({
        selectedCategory,
        searchTerm,
        barcode,
        internalCode,
        somenteComPromocoes: promotionFilter,
        tiposUnidade: selectedUnitTypes.length > 0 ? selectedUnitTypes : undefined,
        categorias,
        pageSize,
        autoSearch: false // Busca manual via botão
    });

    // Mapa combinado de vinculações - FONTE ÚNICA DE VERDADE
    // Combina vinculacoesProdutos (do JSON da API) com mudanças locais (recentlyLinked/Unlinked)
    const vinculacoesCombinadas = useMemo(() => {
        const mapa = new Map<number, boolean>();

        // 1. Primeiro, copiar todos os valores do JSON (base)
        vinculacoesProdutos.forEach((valor, produtoId) => {
            mapa.set(produtoId, valor);
        });

        // 2. Aplicar mudanças locais (sobrescrevem os valores do JSON)
        // Produtos vinculados recentemente ficam como true
        recentlyLinkedProducts.forEach(produtoId => {
            mapa.set(produtoId, true);
        });

        // Produtos desvinculados recentemente ficam como false
        recentlyUnlinkedProducts.forEach(produtoId => {
            mapa.set(produtoId, false);
        });

        return mapa;
    }, [vinculacoesProdutos, recentlyLinkedProducts, recentlyUnlinkedProducts]);

    // IDs de produtos vinculados - usado apenas para filtro de tela específica
    // (mantido para compatibilidade com filtro de tela)
    const linkedProductIdsFromAPI = useMemo(() => {
        const linkedIds = new Set<number>();

        // Extrair apenas produtoIds de todas as telas (já são arrays de números nos dados otimizados)
        telasOtimizadas.forEach(tela => {
            if (tela.produtoIds && Array.isArray(tela.produtoIds)) {
                tela.produtoIds.forEach((produtoId: number) => {
                    if (typeof produtoId === 'number' && produtoId > 0) {
                        linkedIds.add(produtoId);
                    }
                });
            }
        });

        return linkedIds;
    }, [telasOtimizadas]);

    // Quando o campo de busca for limpo (passou de ter valor para vazio), buscar automaticamente
    useEffect(() => {
        const previousSearch = previousSearchTermRef.current;
        const previousBarcode = previousBarcodeRef.current;
        const previousInternal = previousInternalCodeRef.current;
        // Se tinha um termo de busca antes e agora está vazio, buscar automaticamente
        if ((previousSearch.trim() && !searchTerm.trim() && !barcode.trim() && !internalCode.trim()) ||
            (previousBarcode.trim() && !barcode.trim() && !searchTerm.trim() && !internalCode.trim()) ||
            (previousInternal.trim() && !internalCode.trim() && !searchTerm.trim() && !barcode.trim())) {
            triggerSearch();
        }
        // Atualizar referências para próxima verificação
        previousSearchTermRef.current = searchTerm;
        previousBarcodeRef.current = barcode;
        previousInternalCodeRef.current = internalCode;
    }, [searchTerm, barcode, internalCode, triggerSearch]);

    // Verificar se um produto está vinculado
    // NOVA LÓGICA: Usa vinculacoesCombinadas que combina JSON + mudanças locais
    const isPromoLinkedToScreen = useCallback((promoId: number) => {
        // Usar mapa combinado como fonte única de verdade
        return vinculacoesCombinadas.get(promoId) === true;
    }, [vinculacoesCombinadas]);

    // Verificar vinculação ANTES de exibir produtos
    // NOVA LÓGICA: Usa vinculadoATela do JSON, não precisa mais aguardar telas para pesquisa
    // As telas ainda são usadas apenas para vinculação/desvinculação
    useEffect(() => {
        if (loadingProdutos) {
            // Ainda carregando produtos da API - mostrar loading
            setVerificandoVinculacao(true);
        } else if (produtosBuscados.length > 0) {
            // Produtos foram buscados - vinculação já vem no JSON
            // Não precisa aguardar telas para a pesquisa
            setVerificandoVinculacao(false);
        } else {
            // Sem produtos buscados
            setVerificandoVinculacao(false);
        }
    }, [loadingProdutos, produtosBuscados.length]);

    // Filter and sort promotions - OTIMIZADO: primeiro filtra, depois verifica vinculação
    // 1. Filtra produtos da pesquisa (produtosBuscados)
    // 2. Verifica vinculação comparando IDs com linkedProductIdsFromAPI (Set - O(1) lookup)
    // 3. Só retorna produtos após verificação estar completa
    const filteredPromotions = useMemo(() => {
        // Se ainda está verificando vinculação, retornar array vazio (não mostrar nada)
        if (verificandoVinculacao || loadingProdutos) {
            return [];
        }

        // Primeiro: filtrar apenas produtos que estão na pesquisa
        const produtosDaPesquisa = promotions.filter(promo => {
            if (promotionKindFilter === 'product' && promo.isVideoPromotion) return false;
            if (promotionKindFilter === 'video' && !promo.isVideoPromotion) return false;
            return produtosBuscados.includes(promo.id);
        });

        // Segundo: verificar vinculação e aplicar filtros de tela
        return produtosDaPesquisa
            .filter(promo => {
                // NOVA LÓGICA: Verificar vinculação usando mapa combinado (JSON + mudanças locais)
                const isLinked = vinculacoesCombinadas.get(promo.id) === true;

                const matchesScreenFilter =
                    screenFilter === 'all' ? true :
                        screenFilter === 'linked' ? isLinked :
                            !isLinked;

                const matchesSelectedScreen =
                    selectedScreenFilter === 'all' ? true :
                        (() => {
                            // Para filtro de tela específica, verifica nas telas carregadas
                            // Se as telas não foram carregadas, retorna false (não mostra nada)
                            if (telasOtimizadas.length === 0) {
                                return false;
                            }
                            const tela = telasOtimizadas.find(t => t.id === selectedScreenFilter);
                            if (!tela || !tela.produtoIds || !Array.isArray(tela.produtoIds)) {
                                return false;
                            }
                            // Verificação rápida: verificar se produtoId está no array (já são números)
                            return tela.produtoIds.includes(promo.id);
                        })();

                return matchesScreenFilter && matchesSelectedScreen;
            })
            .sort((a, b) => {
                switch (sortOption) {
                    case 'name-asc': return a.name.localeCompare(b.name);
                    case 'name-desc': return b.name.localeCompare(a.name);
                    case 'price-asc': return a.price - b.price;
                    case 'price-desc': return b.price - a.price;
                    default: return 0;
                }
            });
    }, [promotions, screenFilter, selectedScreenFilter, sortOption, promotionKindFilter, telasOtimizadas, produtosBuscados, vinculacoesCombinadas, verificandoVinculacao, loadingProdutos]);


    // Carregar telas apenas quando necessário para vincular produtos
    // Este é o ÚNICO lugar onde carregamos telas agora
    const handleAddToScreen = async (promoId?: number) => {
        if (typeof promoId === 'number') {
            setSinglePromoId(promoId);
            setSelectedPromoIds([]);
        } else {
            setSinglePromoId(null);
        }
        setScreenSelectorMode('link');

        // Verificar se precisa carregar telas e setar loading ANTES de abrir o modal
        const precisaCarregar = telasOtimizadas.length === 0 && !loadingTelas;
        if (precisaCarregar) {
            setLoadingTelas(true);
        }

        // Abrir modal IMEDIATAMENTE para mostrar loading enquanto carrega
        setShowScreenSelector(true);

        // Carregar telas em background (modal já está aberto mostrando loading)
        // OTIMIZAÇÃO: Usar método COM produtos (Produtos=S) para vincular produtos
        if (precisaCarregar) {
            try {
                const telas = await bannerService.listarTelasOtimizadoComProdutos();
                setTelasOtimizadas(telas);
            } catch (err) {
                console.error('Erro ao carregar telas:', err);
            } finally {
                setLoadingTelas(false);
            }
        }
    };

    // Handler para desvincular produtos (múltiplos)
    const handleRemoveFromScreen = async () => {
        setSinglePromoId(null);
        setScreenSelectorMode('unlink');

        // Verificar se precisa carregar telas e setar loading ANTES de abrir o modal
        const precisaCarregar = telasOtimizadas.length === 0 && !loadingTelas;
        if (precisaCarregar) {
            setLoadingTelas(true);
        }

        // Abrir modal IMEDIATAMENTE para mostrar loading enquanto carrega
        setShowScreenSelector(true);

        // Carregar telas em background (modal já está aberto mostrando loading)
        // OTIMIZAÇÃO: Usar método COM produtos (Produtos=S) para vincular produtos
        if (precisaCarregar) {
            try {
                const telas = await bannerService.listarTelasOtimizadoComProdutos();
                setTelasOtimizadas(telas);
            } catch (err) {
                console.error('Erro ao carregar telas:', err);
            } finally {
                setLoadingTelas(false);
            }
        }
    };

    // Handler para desvincular um produto específico
    const handleRemoveSingleFromScreen = async (promoId: number) => {
        setSinglePromoId(promoId);
        setSelectedPromoIds([]);
        setScreenSelectorMode('unlink');

        // Verificar se precisa carregar telas e setar loading ANTES de abrir o modal
        const precisaCarregar = telasOtimizadas.length === 0 && !loadingTelas;
        if (precisaCarregar) {
            setLoadingTelas(true);
        }

        // Abrir modal IMEDIATAMENTE para mostrar loading enquanto carrega
        setShowScreenSelector(true);

        // Carregar telas em background (modal já está aberto mostrando loading)
        // OTIMIZAÇÃO: Usar método COM produtos (Produtos=S) para vincular produtos
        if (precisaCarregar) {
            try {
                const telas = await bannerService.listarTelasOtimizadoComProdutos();
                setTelasOtimizadas(telas);
            } catch (err) {
                console.error('Erro ao carregar telas:', err);
            } finally {
                setLoadingTelas(false);
            }
        }
    };

    // Carregar telas quando o filtro de tela específica for selecionado
    // Isso permite que o filtro funcione mesmo sem abrir o modal de vinculação
    useEffect(() => {
        if (selectedScreenFilter !== 'all') {
            // Se as telas ainda não foram carregadas, carregar
            // Usar listarTelasOtimizado() que já retorna produtoIds (suficiente para o filtro)
            if (telasOtimizadas.length === 0 && !loadingTelas) {
                setLoadingTelas(true);
                bannerService.listarTelasOtimizado()
                    .then(telas => {
                        setTelasOtimizadas(telas);
                    })
                    .catch(err => {
                        console.error('Erro ao carregar telas:', err);
                    })
                    .finally(() => {
                        setLoadingTelas(false);
                    });
            }

            // Carregar TODOS os produtos da tela selecionada
            // OTIMIZAÇÃO: Usar método com Produtos=S para buscar produtos
            const fetchProductsOfScreen = async () => {
                setLoadingScreenProducts(true);
                try {
                    const produtosOtimizados = await bannerService.buscarProdutosTelaOtimizadoComParametro(selectedScreenFilter);

                    // Mapear para o formato Promotion
                    const mappedProducts: Promotion[] = produtosOtimizados.map(p => {
                        const price = p.vlPreco || 0;
                        const normalPrice = p.vlPrecoNormal || 0;

                        // Só mostrar preço original se for diferente e houver um preço atual válido
                        // Se p.vlPreco é 0 ou nulo, usamos o normalPrice como preço principal
                        const currentPrice = price > 0 ? price : normalPrice;
                        const hasDifferentOriginal = normalPrice > 0 && normalPrice !== currentPrice;

                        return {
                            id: p.produtoId,
                            name: p.nome || `Produto ${p.produtoId}`,
                            price: currentPrice,
                            originalPrice: hasDifferentOriginal ? normalPrice : undefined,
                            unit: p.tipoUnidade || 'UN',
                            category: 'Geral', // Categoria não vem no otimizado, usar Geral
                            validUntil: new Date().toISOString(), // Data dummy
                            description: `Código: ${p.produtoId}`,
                            isVideoPromotion: false,
                            hasAudio: false,
                            autoplay: false,
                            loop: false,
                            createdAt: new Date().toISOString()
                        };
                    });

                    setScreenProducts(mappedProducts);
                    // Resetar para primeira página ao mudar de tela (usando triggerSearch para resetar currentPage do hook)
                    triggerSearch();
                } catch (error) {
                    console.error('Erro ao carregar produtos da tela:', error);
                    setScreenProducts([]);
                } finally {
                    setLoadingScreenProducts(false);
                }
            };

            fetchProductsOfScreen();
        } else {
            setScreenProducts([]);
        }
    }, [selectedScreenFilter, telasOtimizadas.length, loadingTelas, carregarTelas]);


    const handleScreenSelection = (screenId: number) => {
        const screen = screens.find(s => s.id === screenId);
        if (screen) {
            // Obter estado atual dos produtos vinculados APENAS da API (dados otimizados)
            const tela = telasOtimizadas.find(t => t.id === screenId);
            const currentLinkedIds = new Set<number>();

            // Verificar APENAS na API (produtoIds já são números nos dados otimizados)
            if (tela?.produtoIds && Array.isArray(tela.produtoIds)) {
                tela.produtoIds.forEach((produtoId: number) => {
                    if (typeof produtoId === 'number' && produtoId > 0) {
                        currentLinkedIds.add(produtoId);
                    }
                });
            }

            // Criar snapshot do produto atual para salvar no store
            const createProductSnapshot = (promoId: number): LinkedProduct | null => {
                const product = promotions.find(p => p.id === promoId);
                if (!product) return null;

                return {
                    id: product.id,
                    name: product.name,
                    price: product.price,
                    originalPrice: product.originalPrice,
                    unit: product.unit,
                    image: product.image,
                    video: product.video,
                    description: product.description,
                    category: product.category,
                    validUntil: product.validUntil,
                    linkedAt: new Date().toISOString(),
                    isVideoPromotion: product.isVideoPromotion,
                    hasAudio: product.hasAudio,
                    autoplay: product.autoplay,
                    loop: product.loop
                };
            };

            if (singlePromoId !== null) {
                // Verificar se está vinculado nesta tela específica
                const wasLinked = currentLinkedIds.has(singlePromoId);

                // Começar com TODOS os produtos da API (manter base)
                const updatedPromotions = new Set<number>(currentLinkedIds);

                // Aplicar mudança: adicionar ou remover o produto específico
                if (wasLinked) {
                    updatedPromotions.delete(singlePromoId);
                } else {
                    updatedPromotions.add(singlePromoId);
                }

                // Atualizar linkedProducts (snapshots) no store local
                const currentLinkedProducts = screen.linkedProducts || [];
                let updatedLinkedProducts: LinkedProduct[] = [];

                if (wasLinked) {
                    // Remover snapshot do produto desvinculado
                    updatedLinkedProducts = currentLinkedProducts.filter(p => p.id !== singlePromoId);
                } else {
                    // Adicionar snapshot do produto vinculado
                    const snapshot = createProductSnapshot(singlePromoId);
                    if (snapshot) {
                        // Verificar se já existe (não duplicar)
                        if (!currentLinkedProducts.find(p => p.id === singlePromoId)) {
                            updatedLinkedProducts = [...currentLinkedProducts, snapshot];
                        } else {
                            updatedLinkedProducts = currentLinkedProducts;
                        }
                    } else {
                        updatedLinkedProducts = currentLinkedProducts;
                    }
                }

                // Atualizar apenas no store local - a API será salva quando clicar em "Salvar"
                updateScreen(screenId, {
                    promotions: Array.from(updatedPromotions),
                    linkedProducts: updatedLinkedProducts
                });
            } else {
                // Começar com TODOS os produtos da API (manter base)
                const currentPromotions = new Set<number>(currentLinkedIds);
                const currentLinkedProducts = screen.linkedProducts || [];
                let updatedLinkedProducts = [...currentLinkedProducts];

                selectedPromoIds.forEach(promoId => {
                    // Verificar se está vinculado nesta tela específica
                    const isCurrentlyLinked = currentLinkedIds.has(promoId);

                    if (isCurrentlyLinked) {
                        // Desvincular
                        currentPromotions.delete(promoId);
                        updatedLinkedProducts = updatedLinkedProducts.filter(p => p.id !== promoId);
                    } else {
                        // Vincular - criar snapshot
                        currentPromotions.add(promoId);
                        const snapshot = createProductSnapshot(promoId);
                        if (snapshot) {
                            // Verificar se já existe (não duplicar)
                            if (!updatedLinkedProducts.find(p => p.id === promoId)) {
                                updatedLinkedProducts.push(snapshot);
                            }
                        }
                    }
                });

                // Atualizar apenas no store local - a API será salva quando clicar em "Salvar"
                updateScreen(screenId, {
                    promotions: Array.from(currentPromotions),
                    linkedProducts: updatedLinkedProducts
                });
            }
        }
    };

    const togglePromoSelection = (promoId: number) => {
        setSelectedPromoIds(prev =>
            prev.includes(promoId)
                ? prev.filter(id => id !== promoId)
                : [...prev, promoId]
        );
    };

    // Determinar se estamos no modo "tela específica"
    const isScreenMode = selectedScreenFilter !== 'all';

    // Produtos da tela filtrados e paginados (modo tela específica)
    const {
        filteredScreenProducts,
        totalScreenItems,
        totalScreenPages,
        safeScreenPage,
        currentScreenPageProducts
    } = useMemo(() => {
        if (!isScreenMode) {
            return {
                filteredScreenProducts: [] as Promotion[],
                totalScreenItems: 0,
                totalScreenPages: 1,
                safeScreenPage: currentPage,
                currentScreenPageProducts: [] as Promotion[]
            };
        }

        const filtered = screenProducts
            .filter(p => {
                if (searchTerm.trim()) {
                    const term = searchTerm.toLowerCase();
                    const matchesName = p.name.toLowerCase().includes(term);
                    const matchesId = p.id.toString().includes(term);
                    if (!matchesName && !matchesId) return false;
                }
                if (barcode.trim()) {
                    // Campo de código de barras não está presente no mapeamento atual
                    // Mantido para futura extensão
                }
                return true;
            })
            .sort((a, b) => {
                switch (sortOption) {
                    case 'name-asc': return a.name.localeCompare(b.name);
                    case 'name-desc': return b.name.localeCompare(a.name);
                    case 'price-asc': return a.price - b.price;
                    case 'price-desc': return b.price - a.price;
                    default: return 0;
                }
            });

        const totalItems = filtered.length;
        const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
        const safePage = Math.min(Math.max(1, currentPage), totalPages);
        const startIndex = (safePage - 1) * pageSize;
        const endIndex = Math.min(startIndex + pageSize, totalItems);
        const currentPageItems = filtered.slice(startIndex, endIndex);

        return {
            filteredScreenProducts: filtered,
            totalScreenItems: totalItems,
            totalScreenPages: totalPages,
            safeScreenPage: safePage,
            currentScreenPageProducts: currentPageItems
        };
    }, [isScreenMode, screenProducts, searchTerm, barcode, sortOption, pageSize, currentPage]);

    // IDs visíveis na página atual (considera modo normal e modo tela)
    const visiblePromotionIds = useMemo<number[]>(() => {
        if (isScreenMode) {
            return currentScreenPageProducts.map(p => p.id);
        }
        return filteredPromotions.map(p => p.id);
    }, [isScreenMode, currentScreenPageProducts, filteredPromotions]);

    // Verificar se todos os visíveis estão selecionados (não usar apenas comparação de tamanhos)
    const areAllVisibleSelected = useMemo(() => {
        if (visiblePromotionIds.length === 0) return false;
        const selectedSet = new Set(selectedPromoIds);
        return visiblePromotionIds.every(id => selectedSet.has(id));
    }, [visiblePromotionIds, selectedPromoIds]);

    // Alternar seleção considerando apenas itens visíveis na página atual
    const toggleAllPromos = () => {
        if (visiblePromotionIds.length === 0) {
            return;
        }
        if (areAllVisibleSelected) {
            // Desmarcar apenas os itens visíveis
            setSelectedPromoIds(prev => prev.filter(id => !visiblePromotionIds.includes(id)));
        } else {
            // Adicionar os itens visíveis sem duplicar
            setSelectedPromoIds(prev => {
                const set = new Set(prev);
                visiblePromotionIds.forEach(id => set.add(id));
                return Array.from(set);
            });
        }
    };

    const handleImportPromotions = (importedData: any) => {
        const validationError = validateImportedData(importedData, 'promotions');

        if (validationError) {
            alert(`Erro nos dados importados: ${validationError}`);
            return;
        }

        importedData.forEach((promoData: any) => {
            const existingPromo = promotions.find(p => p.id === promoData.id);

            if (existingPromo) {
                updatePromotion(promoData.id, promoData);
            } else {
                addPromotion(promoData, promoData.id);
            }
        });
    };

    return (
        <div>
            <PromotionActions
                selectedCount={selectedPromoIds.length}
                onAddToScreen={() => handleAddToScreen()}
                onRemoveFromScreen={handleRemoveFromScreen}
                onNewPromotion={() => setShowForm(true)}
                onImport={handleImportPromotions}
                onSync={syncWithCheckout}
                promotions={promotions}
                isSyncing={isSyncing}
            />

            <PromotionFilters
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                onSearchClick={triggerSearch}
                barcode={barcode}
                onBarcodeChange={setBarcode}
                internalCode={internalCode}
                onInternalCodeChange={setInternalCode}
                selectedCategory={selectedCategory}
                onCategoryChange={setSelectedCategory}
                promotionKindFilter={promotionKindFilter}
                onPromotionKindChange={setPromotionKindFilter}
                promotionFilter={promotionFilter}
                onPromotionFilterChange={setPromotionFilter}
                screenFilter={screenFilter}
                onScreenFilterChange={setScreenFilter}
                selectedScreenFilter={selectedScreenFilter}
                onSelectedScreenChange={setSelectedScreenFilter}
                sortOption={sortOption}
                onSortChange={setSortOption}
                screens={screens}
                allSelected={areAllVisibleSelected}
                onToggleAll={toggleAllPromos}
                categorias={categorias}
                loadingProdutos={loadingProdutos}
                loadingLinked={verificandoVinculacao}
                optimizedScreens={telasOtimizadas}
                selectedUnitTypes={selectedUnitTypes}
                onUnitTypeChange={setSelectedUnitTypes}
            />

            {selectedScreenFilter === 'all' ? (
                // LÓGICA PADRÃO: Paginação via API (PromotionPagination e PromotionList originais)
                <>
                    <PromotionPagination
                        currentPage={currentPage}
                        totalItems={filteredPromotions.length} // filteredPromotions.length é apenas da página atual? Não, produtosBuscados tem tudo? hook diz totalItems
                        pageSize={pageSize}
                        onPageSizeChange={(newPageSize) => {
                            setPageSize(newPageSize);
                        }}
                        loading={loadingProdutos}
                        loadingLinked={verificandoVinculacao}
                        onNextPage={goToNextPage}
                        onPreviousPage={goToPreviousPage}
                        hasNextPage={hasNextPage}
                        hasPreviousPage={hasPreviousPage}
                    />

                    <PromotionList
                        promotions={filteredPromotions}
                        selectedIds={selectedPromoIds}
                        onToggleSelection={togglePromoSelection}
                        onAddToScreen={handleAddToScreen}
                        onRemoveFromScreen={handleRemoveSingleFromScreen}
                        isLinked={isPromoLinkedToScreen}
                        loading={loadingProdutos || verificandoVinculacao}
                    />
                </>
            ) : (
                // LÓGICA DE TELA ESPECÍFICA: Paginação local dos produtos da tela
                <>
                    <PromotionPagination
                        currentPage={safeScreenPage}
                        totalItems={totalScreenItems}
                        pageSize={pageSize}
                        onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                        loading={loadingScreenProducts}
                        loadingLinked={false}
                        onNextPage={() => {
                            goToNextPage();
                        }}
                        onPreviousPage={() => goToPreviousPage()}
                        hasNextPage={safeScreenPage < totalScreenPages}
                        hasPreviousPage={safeScreenPage > 1}
                    />

                    <PromotionList
                        promotions={currentScreenPageProducts}
                        selectedIds={selectedPromoIds}
                        onToggleSelection={togglePromoSelection}
                        onAddToScreen={handleAddToScreen}
                        onRemoveFromScreen={handleRemoveSingleFromScreen}
                        isLinked={() => true} // Se está na lista da tela, está vinculado
                        loading={loadingScreenProducts}
                    />
                </>
            )}

            {showForm && (
                <PromotionFormModal
                    editingPromo={null}
                    onClose={() => {
                        setShowForm(false);
                    }}
                    onSave={(promoData, id) => {
                        addPromotion(promoData, id);
                        setShowForm(false);
                    }}
                />
            )}

            {showScreenSelector && (
                <ScreenSelectorModal
                    screens={screens}
                    promotions={promotions}
                    singlePromoId={singlePromoId}
                    selectedPromoIds={selectedPromoIds}
                    mode={screenSelectorMode}
                    telasOtimizadasProp={telasOtimizadas} // Passar telas já carregadas para evitar requisição duplicada
                    loadingTelasProp={loadingTelas} // Passar estado de loading para mostrar "Carregando telas..." imediatamente
                    onSelect={handleScreenSelection}
                    onSaveToAPI={async (linkedProductIds?: number[], unlinkedProductIds?: number[], screensModified?: Map<number, number[]>) => {
                        // Atualizar estado local dos produtos vinculados/desvinculados
                        if (linkedProductIds && linkedProductIds.length > 0) {
                            setRecentlyLinkedProducts(prev => {
                                const newSet = new Set(prev);
                                linkedProductIds.forEach(id => newSet.add(id));
                                return newSet;
                            });
                            // Remover dos desvinculados se estava lá
                            setRecentlyUnlinkedProducts(prev => {
                                const newSet = new Set(prev);
                                linkedProductIds.forEach(id => newSet.delete(id));
                                return newSet;
                            });
                        }

                        if (unlinkedProductIds && unlinkedProductIds.length > 0) {
                            setRecentlyUnlinkedProducts(prev => {
                                const newSet = new Set(prev);
                                unlinkedProductIds.forEach(id => newSet.add(id));
                                return newSet;
                            });
                            // Remover dos vinculados se estava lá
                            setRecentlyLinkedProducts(prev => {
                                const newSet = new Set(prev);
                                unlinkedProductIds.forEach(id => newSet.delete(id));
                                return newSet;
                            });
                        }

                        // Atualizar estado local das telas (telasOtimizadas) para refletir mudanças imediatamente
                        if (screensModified && screensModified.size > 0) {
                            setTelasOtimizadas(prev => {
                                return prev.map(tela => {
                                    const produtosAtualizados = screensModified.get(tela.id);
                                    if (produtosAtualizados) {
                                        // Atualizar produtos desta tela
                                        return {
                                            ...tela,
                                            produtoIds: produtosAtualizados
                                        };
                                    }
                                    return tela;
                                });
                            });

                            // Se estamos filtrando por uma tela específica, REFETCHAR da API para garantir estado real
                            if (selectedScreenFilter !== 'all') {
                                try {
                                    setLoadingScreenProducts(true);

                                    // 1) Atualizar a lista de produtos dessa tela a partir da API (Produtos=S)
                                    const produtosOtimizados = await bannerService.buscarProdutosTelaOtimizadoComParametro(selectedScreenFilter as number);

                                    const mappedProducts: Promotion[] = produtosOtimizados.map(p => {
                                        const price = p.vlPreco || 0;
                                        const normalPrice = p.vlPrecoNormal || 0;
                                        const currentPrice = price > 0 ? price : normalPrice;
                                        const hasDifferentOriginal = normalPrice > 0 && normalPrice !== currentPrice;

                                        return {
                                            id: p.produtoId,
                                            name: p.nome || `Produto ${p.produtoId}`,
                                            price: currentPrice,
                                            originalPrice: hasDifferentOriginal ? normalPrice : undefined,
                                            unit: p.tipoUnidade || 'UN',
                                            category: 'Geral',
                                            validUntil: new Date().toISOString(),
                                            description: `Código: ${p.produtoId}`,
                                            isVideoPromotion: false,
                                            hasAudio: false,
                                            autoplay: false,
                                            loop: false,
                                            createdAt: new Date().toISOString()
                                        };
                                    });

                                    setScreenProducts(mappedProducts);

                                    // 2) Atualizar os produtoIds dessa tela em telasOtimizadas a partir da API oficial (fonte da verdade)
                                    const telasAtualizadas = await bannerService.listarTelasOtimizado(selectedScreenFilter as number);
                                    const telaAtualizada = telasAtualizadas.find(t => t.id === (selectedScreenFilter as number));
                                    if (telaAtualizada) {
                                        setTelasOtimizadas(prev => {
                                            return prev.map(t =>
                                                t.id === telaAtualizada.id
                                                    ? { ...t, produtoIds: telaAtualizada.produtoIds }
                                                    : t
                                            );
                                        });
                                    }
                                } catch (err) {
                                    console.error('Erro ao recarregar produtos/tela após salvar:', err);
                                } finally {
                                    setLoadingScreenProducts(false);
                                }
                            }
                        }

                        // Desmarcar todos os produtos após vincular/desvincular com sucesso
                        setSelectedPromoIds([]);
                        setSinglePromoId(null);
                    }}
                    onClose={() => {
                        setShowScreenSelector(false);
                        setSinglePromoId(null);
                    }}
                />
            )}

        </div>
    );
};

export default PromotionsPage;
